import React, { lazy, Suspense, useCallback, useContext } from 'react'
import { Route, Routes } from 'react-router-dom'

import { ErrorContext } from 'src/store/ErrorContext'
import { ConfigContext } from 'src/store/ConfigContext'
import { RESULT } from 'src/config/constants'

import { HPAllInContext } from '../../store/HPAllInContext'
import { ANALYTICS, ENROLLMENT_OUTPUT } from '../../config/constants'
import HPAllInMain from '../../pages/HPAllInMain'

const HPAllInError = lazy(
  () =>
    import(/* webpackChunkName: "hpallin-error" */ '../../pages/HPAllInError')
)

const ActivationFailed = lazy(
  () =>
    import(
      /* webpackChunkName: "hpallin-activation-failed" */ 'src/pages/ActivationFailed'
    )
)

const PortalEntry = lazy(
  () =>
    import(
      /* webpackChunkName: "hpallin-portal-entry" */ '../../pages/PortalEntry'
    )
)

const WebServicesCdm = lazy(
  () =>
    import(
      /* webpackChunkName: "hpallin-ws-cdm" */ '../../pages/WebServicesCdm'
    )
)

const WebServicesLedm = lazy(
  () =>
    import(
      /* webpackChunkName: "hpallin-ws-ledm" */ '../../pages/WebServicesLedm'
    )
)

/**
 * HPAllInDeviceOnboarding orchestrates the full flow:
 * 1. Shows HPAllInMain (loader screen with CMS content) during the process
 * 2. PortalEntry verifies subscription state, fires PATCH, then routes to WSE
 * 3. WebServicesCdm / WebServicesLedm handle the actual web services enablement
 * 4. On subscription state error → HPAllInError
 * 5. On WSE error → ActivationFailed (shared component)
 */
const HPAllInDeviceOnboarding = () => {
  const { error } = useContext(ErrorContext)
  const { subscriptionError } = useContext(HPAllInContext)
  const { closeServiceInstance } = useContext(ConfigContext)

  const onActivationFailedExit = useCallback(() => {
    closeServiceInstance(RESULT.FAILURE, {
      user: {
        subscriptionStateSvc: { enrollmentStatus: ENROLLMENT_OUTPUT.FAILURE }
      }
    })
  }, [closeServiceInstance])

  if (subscriptionError) {
    return (
      <Suspense fallback={<></>}>
        <HPAllInError errorType={subscriptionError} />
      </Suspense>
    )
  }

  if (error) {
    return (
      <Suspense fallback={<></>}>
        <ActivationFailed
          error={error}
          analytics={ANALYTICS}
          onExit={onActivationFailedExit}
        />
      </Suspense>
    )
  }

  return (
    <>
      <HPAllInMain />
      <Routes>
        <Route
          path="cdm"
          element={
            <Suspense fallback={<></>}>
              <WebServicesCdm />
            </Suspense>
          }
        />
        <Route
          path="ledm"
          element={
            <Suspense fallback={<></>}>
              <WebServicesLedm />
            </Suspense>
          }
        />
        <Route
          exact
          path="/"
          element={
            <Suspense fallback={<></>}>
              <PortalEntry />
            </Suspense>
          }
        />
      </Routes>
    </>
  )
}

export default HPAllInDeviceOnboarding
